import { Link, RouteComponentProps } from '@reach/router';
import {
  LessonPlan,
  LessonPlanEquipment,
  LessonPlanResource,
  LessonsApi,
} from 'api/lessonsApi';
import LessonDetailTop from 'components/lessons/LessonDetailTop';
import LessonPageStatus from 'components/lessons/LessonPageStatus';
import {
  LessonPrintParts,
  useLessonPrint,
} from 'components/lessons/LessonPrint';
import {
  closedAccessButton,
  downloadFileInPlace,
  equipmentImage,
  escapeHtml,
  LessonsRouteParams,
  openFileInNewTab,
  PRESENTATION_IMAGE,
  resourceTypeImage,
  SUBSCRIBERS_ONLY_BUTTON,
  unitPath,
  useLessonsPage,
} from 'components/lessons/lessonsHelpers';
import SubjectSiteLayout from 'components/subjectSite/SubjectSiteLayout';
import useEqualHeights from 'components/subjectSite/useEqualHeights';
import ActivityDefaultImage from 'images/subjectSite/Card_Default.png';
import React, { useRef, useState } from 'react';

type Props = RouteComponentProps<LessonsRouteParams>;

/**
 * A BtnTwo button run by a click
 */
const ActionButton: React.FC<{ onClick: () => void }> = ({
  onClick,
  children,
}) => (
  <div className="BtnTwo BtnOpen">
    {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
    <a
      href="#"
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
    >
      {children}
    </a>
  </div>
);

const LessonPlanPage: React.FC<Props> = (props) => {
  const result = useLessonsPage<LessonPlan>(props, (query, lessonNumber) =>
    LessonsApi.getLessonPlan(query, lessonNumber)
  );
  const subjectsId =
    result && typeof result !== 'string' ? result.subject.subjectsID : null;
  const { print, popup, bError } = useLessonPrint(subjectsId);
  const [bFileError, setBFileError] = useState(false);
  const requiredListRef = useRef<HTMLDivElement>(null);
  const optionalListRef = useRef<HTMLDivElement>(null);
  const details = result && typeof result !== 'string' ? result.details : null;

  useEqualHeights(requiredListRef, '.ItemList01', '.TxtList01', details);
  useEqualHeights(optionalListRef, '.ItemList02', '.TxtList02', details);

  if (result === null || typeof result === 'string') {
    return <LessonPageStatus status={result} />;
  }

  const { subject, labels, query, details: lessonPlan } = result;
  const { lesson, activity, access, keyStageName, successCriterias } =
    lessonPlan;
  const path = unitPath(
    subject.internalCode,
    lessonPlan.keyStage.strippedName,
    activity.strippedName
  );
  const lessonPath = (lessonNumber: number) =>
    `${path}/${lessonNumber}?years=${query.years}`;
  const wholeChildObjectives = lesson.wholeChildObjectives?.trim()
    ? lesson.wholeChildObjectives.split('\n')
    : [];
  const bEYFS = keyStageName.toLowerCase() === 'eyfs';
  const criteriaYears = Array.from(
    new Set(successCriterias.map((item) => item.applicableYearsNames ?? ''))
  );

  const openFile = (
    getUrl: () => Promise<string | null>,
    bDownload = false
  ) => {
    setBFileError(false);
    (bDownload ? downloadFileInPlace(getUrl) : openFileInNewTab(getUrl)).then(
      (bOpened) => setBFileError(!bOpened)
    );
  };

  const criteriaItems = (items: typeof successCriterias) =>
    items.map((item, index) => (
      <div key={index} className="Item">
        <span className="BulletPoint">•</span>
        {item.criteria}
      </div>
    ));

  const criteriaByYear = criteriaYears.map((yearNames) => {
    const yearCriteria = successCriterias.filter(
      (item) => (item.applicableYearsNames ?? '') === yearNames
    );
    const ids = Array.from(
      new Set(yearCriteria.map((item) => item.assessmentSuccessCriteriasId))
    );

    return (
      <React.Fragment key={yearNames}>
        <div className="Year">{criteriaYears.length > 1 ? yearNames : ''}</div>
        {ids.map((id) => (
          <div key={id} className="List">
            {criteriaItems(
              yearCriteria.filter(
                (item) => item.assessmentSuccessCriteriasId === id
              )
            )}
          </div>
        ))}
      </React.Fragment>
    );
  });

  const wholeChildPanel = (criteria: React.ReactNode) => (
    <div className="Panel_Two">
      <div className="Cell_Left">
        <div className="WidthCell">
          <div className="Inner">
            <div className="SubTitle">{labels.SuccessCriteria}</div>
            <div className="SubLine"></div>
            {criteria}
          </div>
        </div>
      </div>
      <div
        className="Cell_Right"
        style={{
          backgroundImage: `url(${
            lesson.objectivesImagePath || ActivityDefaultImage
          })`,
        }}
      >
        <div className="WidthCell">
          <div className="Inner">
            <div className="SubTitle">Whole Child Objectives</div>
            <div className="SubLine"></div>
            <div className="List">
              {wholeChildObjectives.map((item, index) => (
                <div key={index} className="Item">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const criteriaPanel = (criteria: React.ReactNode) => (
    <div className="Panel_Four">
      <div className="Inner">
        <div className="WidthContent">
          <div className="Subtitle">{labels.SuccessCriteria}</div>
          <div className="SubLine"></div>
          {criteria}
        </div>
      </div>
    </div>
  );

  let criteriaSection: React.ReactNode = null;

  if (successCriterias.length > 0) {
    if (wholeChildObjectives.length > 0)
      criteriaSection = wholeChildPanel(criteriaByYear);
    else if (bEYFS)
      criteriaSection = criteriaPanel(
        <div className="List">{criteriaItems(successCriterias)}</div>
      );
    else criteriaSection = criteriaPanel(criteriaByYear);
  } else if (wholeChildObjectives.length > 0) {
    criteriaSection = wholeChildPanel(null);
  }

  const equipmentItem = (
    item: LessonPlanEquipment,
    listNumber: '01' | '02',
    bButtons: boolean
  ) => (
    <div
      key={item.virtualPECupboardsId}
      className={`Item_E ItemList${listNumber}`}
    >
      <div className="Table_E">
        <div className="Img_E">
          <img src={equipmentImage(item)} alt={item.name} />
        </div>
        <div className={`Desc_E TxtList${listNumber}`}>
          <div className="Txt_E">{item.name}</div>
          {item.pupilsPerItem?.trim() && (
            <div className="Nbre_E">{`x ${item.pupilsPerItem}`}</div>
          )}
          {bButtons && item.bIsResourceCard && (
            <div className="BottomButtons">
              {[false, true].map((bDownload) => (
                <ActionButton
                  key={String(bDownload)}
                  onClick={() =>
                    openFile(
                      () =>
                        LessonsApi.getEquipmentFileUrl(
                          subject.subjectsID,
                          item.equipmentsId,
                          bDownload
                        ),
                      bDownload
                    )
                  }
                >
                  {bDownload ? 'Download' : 'Open'}
                </ActionButton>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const resourceButtons = (resource: LessonPlanResource) => {
    const getUrl = (bDownload: boolean) => () =>
      LessonsApi.getResourceFileUrl(
        subject.subjectsID,
        resource.resourcesId,
        bDownload
      );

    if (!resource.bCanOpen && !resource.bCanDownload) {
      const button = access.bFullAccess
        ? SUBSCRIBERS_ONLY_BUTTON
        : closedAccessButton(access);
      return (
        <div className="BtnTwo BtnOpen">
          {button.href ? (
            <Link to={button.href}>{button.text}</Link>
          ) : (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <a>{button.text}</a>
          )}
        </div>
      );
    }

    return (
      <>
        {resource.bCanOpen && (
          <ActionButton onClick={() => openFile(getUrl(false))}>
            Open
          </ActionButton>
        )}
        {resource.bCanDownload && (
          <ActionButton onClick={() => openFile(getUrl(true), true)}>
            Download
          </ActionButton>
        )}
      </>
    );
  };

  const planItems = [
    {
      heading: labels.WarmUpAndIntroduction,
      text: lesson.warmUpAndIntroduction,
      bParagraph: false,
    },
    {
      heading: 'Develop and Apply',
      text: lesson.skillDevelopment,
      bParagraph: false,
    },
    {
      heading: 'Creating and Performing the Dance',
      text: lesson.creatingPerformingTheDance,
      bParagraph: true,
    },
    { heading: 'Reflect and Review', text: lesson.plenary, bParagraph: true },
  ].filter((item) => item.text && item.text.trim() !== '');

  return (
    <SubjectSiteLayout colorCss={activity.colorCSS}>
      <div className="LessonDetail LessonPlan">
        <LessonPrintParts schoolName={lessonPlan.schoolName} />
        <div className="PrintHideFromView">
          <LessonDetailTop
            activity={activity}
            keyStageName={keyStageName}
            backPath={`${path}?years=${query.years}`}
            lessonLine={`Lesson ${lesson.lessonNumber}${
              lesson.theme ? ` - ${lesson.theme}` : ''
            }`}
            onPrint={print}
            printError={
              bError && (
                <span className="ValidationInfo">
                  An error occurred, please try again.
                </span>
              )
            }
          >
            <div className="BackForwardBtns">
              {lessonPlan.previousLesson && (
                <Link
                  className="BtnWithArrow Back"
                  to={lessonPath(lessonPlan.previousLesson.lessonNumber)}
                >
                  <div className="Icon"></div>
                  <div className="Txt">
                    Previous <span className="HideSm">Lesson</span>
                  </div>
                </Link>
              )}
              {lessonPlan.nextLesson && (
                <Link
                  className="BtnWithArrow Forward"
                  to={lessonPath(lessonPlan.nextLesson.lessonNumber)}
                >
                  <div className="Txt">
                    Next <span className="HideSm">Lesson</span>
                  </div>
                  <div className="Icon"></div>
                </Link>
              )}
              <div className="BottomFix"></div>
            </div>
          </LessonDetailTop>

          <div className="Panel_One LearningObjPanel" id="Panel_One">
            <div className="Inner">
              <div className="WidthContent">
                <div className="Subtitle">Learning Objective</div>
                {lesson.learningObjectives.map((item, index) => (
                  <h2 key={index} className="Txt">
                    {item.objective}
                  </h2>
                ))}
              </div>
            </div>
          </div>

          {criteriaSection}

          <div className="Panel_Three">
            <div className="Inner">
              <div className="WidthContent">
                <div className="Subtitle">{labels.Equipment}</div>
                <div
                  className="WarpperEquipment2 WrapperList01"
                  ref={requiredListRef}
                >
                  {lessonPlan.presentationResources.length > 0 && (
                    <div className="Item_E ItemList01">
                      <div className="Table_E Presentation">
                        <div className="Img_E">
                          <img src={PRESENTATION_IMAGE} alt="Presentation" />
                        </div>
                        <div className="Desc_E TxtList01">
                          <div className="Txt_E">
                            {lessonPlan.presentationName}
                          </div>
                          <div className="BottomButtons">
                            <div className="BtnTwo BtnOpen">
                              <a
                                href={`${path}/${lesson.lessonNumber}/presentation?years=${query.years}`}
                                target="_blank"
                                rel="noreferrer"
                              >
                                Open
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {lessonPlan.compulsoryEquipment.map((item) =>
                    equipmentItem(item, '01', true)
                  )}
                  {lessonPlan.resources.map((resource) => (
                    <div
                      key={resource.resourcesId}
                      className="Item_E ItemList01"
                    >
                      <div className="Table_E">
                        <div className="Img_E">
                          <img
                            src={resourceTypeImage(resource.resourceType)}
                            alt={resource.resourceType}
                          />
                        </div>
                        <div className="Desc_E TxtList01">
                          <div className="Txt_E">{resource.name}</div>
                          <div className="Nbre_E">{resource.resourceType}</div>
                          <div className="BottomButtons">
                            {resourceButtons(resource)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {bFileError && (
                  <span className="ValidationInfo">
                    An error occurred, please try again.
                  </span>
                )}

                {lessonPlan.optionalEquipment.length > 0 && (
                  <>
                    <div className="SmallTitle">Optional:</div>
                    <div
                      className="WarpperEquipment WrapperList02"
                      ref={optionalListRef}
                    >
                      {lessonPlan.optionalEquipment.map((item) =>
                        equipmentItem(item, '02', false)
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="Panel_Cms">
            <div className="Inner">
              <div className="WidthContent">
                <div className="WrapperPlan">
                  {planItems.map((item) => (
                    <div
                      key={item.heading}
                      className="ItemPlan"
                      dangerouslySetInnerHTML={{
                        __html: `<h2 class="Subtitle">${escapeHtml(
                          item.heading ?? ''
                        )}</h2><div class="BottomFix"></div>${
                          item.bParagraph ? `<p>${item.text}</p>` : item.text
                        }`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {popup}
    </SubjectSiteLayout>
  );
};

export default LessonPlanPage;
